import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/AppConfig';
import { Footer, ProductCard, ProductDetailsView } from './Home';
import { FaArrowLeft } from 'react-icons/fa';

export const CategoryProducts = () => {
  const { categoryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const categoryName = location.state?.categoryName || 'Products';
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [likedProducts, setLikedProducts] = useState(new Set());
  const [showOwnerDetails, setShowOwnerDetails] = useState(false);
  
  const token = sessionStorage.getItem("token");
  const isLoggedIn = !!token;
  const shouldShowFooter = !token;

  const fetchProductsByCategory = () => {
    setLoading(true);
  
    const user_id = sessionStorage.getItem("id") || 0;
    const product_limit = 0; // 0 = unlimited as per backend
  
    axios
      .get(`${API_URL}/get_product_category_wise`, {
        params: {
          user_id: user_id,
          category_id: categoryId,
          product_limit: product_limit,
        },
        headers: {
          Authorization: `Bearer ${token}`, // agar backend require kare
        },
      })
      .then((response) => {
        if (response.data.success) {
          // backend returns product_arr
          setProducts(response.data.product_arr !== "NA" ? response.data.product_arr : []);
        } else {
          setProducts([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProductsByCategory();
  }, [categoryId]);

  const handleLike = (productId, event) => {
    event.stopPropagation();
    
    if (!isLoggedIn) {
      alert("Please login to like products");
      return;
    }

    setLikedProducts((prev) => {
      const newLiked = new Set(prev);
      if (newLiked.has(productId)) {
        newLiked.delete(productId);
      } else {
        newLiked.add(productId);
      }
      return newLiked;
    });

    // API call for like/unlike
    axios
      .post(`${API_URL}/toggle_like`, { product_id: productId }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .catch((error) => console.error("Error toggling like:", error));
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowOwnerDetails(false);
  };

  const handleBack = () => {
    if (selectedProduct) {
      setSelectedProduct(null);
    } else {
      navigate(-1);
    }
  };

  const breadcrumbItems = [
    { label: "Categories", link: "/categories" },
    { label: categoryName, link: `/category/${categoryId}` },
    ...(selectedProduct ? [{ label: selectedProduct.product_name }] : [])
  ];

  // If product detail view is open
  if (selectedProduct) {
    return (
      <ProductDetailsView
        product={selectedProduct}
        liked={likedProducts.has(selectedProduct.product_id)}
        onLike={handleLike}
        onBack={handleBack}
        onToggleOwner={() => setShowOwnerDetails(!showOwnerDetails)}
        showOwnerDetails={showOwnerDetails}
        breadcrumbItems={breadcrumbItems}
        token={token}
      />
    );
  }

  // Product listing view
  return (
    <div className="category-products-page">
      <div className="container">
        <div className="category-header mb-4">
          <button className="btn btn-outline-primary btn-pill" onClick={handleBack}>
            <FaArrowLeft className="me-2" />
            Back to Categories
          </button>
          <h1 className="mt-3">{categoryName}</h1>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state text-center py-5">
            <div className="empty-state__icon mb-4">
              <svg 
                width="120" 
                height="120" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="1.5"
                className="text-muted"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 3v18" />
                <path d="M15 3v18" />
                <path d="M3 9h18" />
                <path d="M3 15h18" />
              </svg>
            </div>
            <h3 className="mb-3">No Products Found</h3>
            <p className="text-muted mb-4">
              There are no products available in this category at the moment.
            </p>
            <button 
              className="btn btn-primary btn-pill"
              onClick={() => navigate(-1)}
            >
              Browse Other Categories
            </button>
          </div>
        ) : (
          <div className="row g-4">
            {products.map((product) => (
              <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={product.product_id}>
                <ProductCard
                  product={product}
                  liked={likedProducts.has(product.product_id)}
                  onLike={handleLike}
                  onClick={() => handleProductClick(product)}
                  isLoggedIn={isLoggedIn}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {shouldShowFooter && <Footer token={token} />}
    </div>
  );
};